import { Box, Button, Typography, CircularProgress, styled, Divider, FormControlLabel, Switch, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { CreditCard } from 'components/CreditCard';
import { Product, ProductsResponse } from 'types/Product';
import FaviconRound from 'icons/FaviconRound.icon';
import PlusGreenIcon from 'icons/PlusGreen.icon';
import NoData from 'icons/NoData.icon';
import { useEffect, useState } from 'react';
import authStore from 'store/authStore';
// import { useAuthStore } from 'store/authStore';

export const Route = createFileRoute('/(dashboard)/_dashboard/credits')({
  component: RouteComponent,
});

const StyledButton = styled(Button)(() => ({
  padding: '8px 8px 8px 12px',
  borderRadius: '8px',
  background: '#fff',
  border: '1px solid var(--Light-Gray)',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '24x',
  color: 'var(--Interviewer-Lightest-Black)',
  boxShadow: 'none',

  '&:hover': {
    background: '#fff',
    boxShadow: 'none',
  },

  '.MuiButton-startIcon': {
    paddingRight: '8px',
    marginRight: '8px',
    borderRight: '1px solid var(--Light-Gray)',
  },
}));


const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  border: "none",
  borderRadius: "100%",
  padding: "6px 24px",
  margin: '1px',
  fontWeight: 600,
  "&.Mui-selected": {
    backgroundColor: "var(--Interviewer-Black)",
    color: "#fff",
  },
  "&:not(.Mui-selected)": {
    backgroundColor: "#fff",
    color: "#999",
  },
}));



function RouteComponent() {

  const [itemsList, setItemsList] = useState([]);
  const [plan, setPlan] = useState('monthly');

  const [productsToShow, setProductsToShow] = useState([])

  const {authData} = authStore();
  const currentUser = authData;

  const { data: products, isLoading, error } = useQuery<ProductsResponse>({
    queryKey: ['products', 'business'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await axios.get<ProductsResponse>(
        'https://interviewer.runasp.net/api/products',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
  });


  const handlePlanChange = (event, newPlan) => {
    if (newPlan !== null) {
      setPlan(newPlan);
    }
  };


  useEffect(() => {
    if (!products?.value) return;
    const productsArray = products.value || [];
    const userRole = currentUser.roles[0];
    
    let roleFilteredProducts = [];
    
    if (userRole === 'Admin') {
      roleFilteredProducts = productsArray.filter(product => product.type === 2);
    } else if (userRole === 'BasicUser') {
      roleFilteredProducts = productsArray.filter(product => product.type === 1);
    } else {
      roleFilteredProducts = [];
    }
    
     const filteredProducts = roleFilteredProducts
       .map(product => {
         const filteredPrices = userRole === 'BasicUser' 
           ? product.prices
           : product.prices.filter(price =>
               plan === 'monthly' ? price.priceType === 4 : price.priceType === 5
             );

         return { ...product, prices: filteredPrices };
       })
       .filter(Boolean);

    setProductsToShow(filteredProducts);

  }, [plan, products, currentUser.roles]);


  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <NoData />
    );
  }

  return (
    <Box sx={{
      padding: {xs: '20px 16px', md: '125px 75px'}
    }}>
      <Typography variant='h1'>Buy Credits</Typography>

      <Divider  sx={{my: '23px'}}/>


      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
        <Typography variant='h2'>Credit Packs</Typography>

        <ToggleButtonGroup
          value={plan}
          exclusive
          onChange={handlePlanChange}
          aria-label="plan toggle"
          color="primary"
          size="large"
          sx={{
            backgroundColor: "#fff",
            borderRadius: "9999px",
            padding: "4px",
            "& .MuiToggleButton-root": {
              border: "none",
              borderRadius: "9999px !important",
            },
          }}
        >
          <StyledToggleButton value="monthly" aria-label="monthly plan">
            Monthly
          </StyledToggleButton>
          <StyledToggleButton value="yearly" aria-label="yearly plan">
            Yearly
          </StyledToggleButton>
        </ToggleButtonGroup>
      </Box>
      
      <Box sx={{
        display: 'grid',
        gap: '24px',
        mt: 3,
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          lg: 'repeat(3, 1fr)',
        },
      }}>

        {productsToShow?.map(product => (
          <CreditCard
            key={product.id}
            product={product}
          />
        ))}
      </Box>
      
      {/* <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: {
          xs: 'column',
          sm: 'row',
        },  
        backgroundColor: '#fff',
        border: '1px solid var(--Light-Gray)',
        boxShadow: '0px 1px 2px 0px #0205111A',
        padding: { xs: '16px', sm: '32px' },
        gap: '20px',
        borderRadius: '24px'
      }}>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
          <Typography variant='h2'>Need a specific amount of credits?</Typography>
          <Typography>We’ve got you!</Typography>
        </Box>
        <Box sx={{
          display: 'flex',  
          flexDirection: {
            xs: 'column',
            sm: 'row',
          }, 
          gap: '12px', 
          alignItems: {
            xs: 'flex-start',
            sm: 'center'
          }
        }}>
          <StyledButton
              variant="contained"
              startIcon={<FaviconRound />}
            >
              e.g '90 credits'
          </StyledButton>
          <Button variant='contained'>Purchase</Button>
        </Box>
      </Box> */}
    </Box>
  )
}
